import { observer } from 'mobx-react-lite'
import { EmptyCell, type IColumn, NameValueRecord, Typography } from 'shared/ui'
import { NumberCell } from 'shared/ui/Table/ui/NumberCell/NumberCell'
import type { PowerDialer } from 'entities/PowerDialer/model/PowerDialer'
import { PowerDialerStatusBadge } from 'widgets/PowerDialer'

export const getColumns = (): IColumn<PowerDialer>[] => {
  const nameColumn: IColumn<PowerDialer> = {
    field: 'name',
    name: 'Name',
    minWidth: '240px',
    width: '100%',
    isDisabledChangeVisible: true,
    renderRowCell: ({ name }) => <NameCell name={name} />,
  }
  const statusColumn: IColumn<PowerDialer> = {
    field: 'status',
    name: 'Status',
    minWidth: '160px',
    isDisabledChangeVisible: true,
    renderRowCell: ({ status }) => <PowerDialerStatusBadge status={status} />,
  }
  const enrolledColumn: IColumn<PowerDialer> = {
    field: 'enrolled',
    name: 'Enrolled',
    tooltipQuestionProps: {
      type: 'description',
      label:
        'Contacts are recalculated when the Power Dialer starts or resumes. You can also refresh manually.',
      placement: 'top',
      desc: ['Applies only to Power Dialers created with filters.'],
      width: '320px',
    },
    renderRowCell: ({ statistics: { enrolled } }) => <NumberCell value={enrolled} />,
  }
  const contactedColumn: IColumn<PowerDialer> = {
    field: 'contacted',
    name: 'Contacted',
    renderRowCell: ({ statistics: { contacted } }) => <NumberCell value={contacted} />,
  }
  const notContactedColumn: IColumn<PowerDialer> = {
    field: 'not_contacted',
    name: 'Not contacted',
    renderRowCell: ({ statistics: { noContacted } }) => <NumberCell value={noContacted} />,
  }
  const answeredColumn: IColumn<PowerDialer> = {
    field: 'answered',
    name: 'Answered',
    isHideByDefault: true,
    renderRowCell: ({ statistics: { answered } }) => <NumberCell value={answered} />,
  }
  const noAnswerColumn: IColumn<PowerDialer> = {
    field: 'no_answer',
    name: 'No answer',
    isHideByDefault: true,
    renderRowCell: ({ statistics: { noAnswered } }) => <NumberCell value={noAnswered} />,
  }
  const skippedColumn: IColumn<PowerDialer> = {
    field: 'skipped_by_user',
    name: 'Skipped by user',
    isHideByDefault: true,
    renderRowCell: ({ statistics: { skipped } }) => <NumberCell value={skipped} />,
  }
  const callFromColumn: IColumn<PowerDialer> = {
    field: 'call_from',
    name: 'Call from',
    width: '250px',
    renderRowCell: (row) =>
      row.sendFromNumbersProps ? <NameValueRecord {...row.sendFromNumbersProps} /> : <EmptyCell />,
  }

  return [
    nameColumn,
    statusColumn,
    enrolledColumn,
    contactedColumn,
    notContactedColumn,
    answeredColumn,
    noAnswerColumn,
    skippedColumn,
    callFromColumn,
  ]
}

const NameCell = observer(({ name }: { name: string }) => (
  <Typography
    variant='body-md-regular'
    ellipsis
    tooltipProps={{
      label: name,
    }}
    ariaLabel='PowerDialerName'
  >
    {name}
  </Typography>
))
